import {
  getObjectiveStrategy,
  getSequenceObjectiveStrategy,
  isSequenceObjective,
  type AnySimulationObjective,
  type SimulationObjective,
} from "@opus-dt/shared-types";
import type { SimulationObjectiveChoice } from "@/types/sim-sandbox.types";

// Metadados de APRESENTAÇÃO do objetivo. O que é regra de negócio (rótulo da métrica,
// ordenação, veredito de meta) vive no registry compartilhado `sim-objectives.ts` e é lido daqui —
// nunca reescrito. O arquivo anterior (`auto/auto-config-constants.ts`) duplicava os três objetivos
// com rótulos próprios, que é a origem clássica de divergência entre worker e tela.
// O que mora aqui e NÃO no registry: a pergunta em português que o usuário lê, o texto de ajuda e
// o rótulo do campo que o objetivo pede. São decisões de tela, não de ranqueamento.

export type ObjectiveField = "window" | "quantity" | "productionOrder";

/** Só os objetivos que a tela oferece hoje — casa 1:1 com a união discriminada da escolha, para
 * que o seletor não consiga produzir um objetivo sem o campo correspondente. */
export type OfferedObjective = SimulationObjectiveChoice["kind"];

/**
 * Como o eixo de desvio deste objetivo é LIDO no ranking.
 *
 * "melhor · pior" é verdadeiro e não diz nada: quem opera a linha pensa em "mais rápido" ou "mais
 * folga", não em bem/mal abstratos. As palavras são de tela (o registry compartilhado guarda a
 * direção matemática em `higherIsBetter`); ficam aqui, junto do resto do vocabulário do objetivo,
 * para não nascer um segundo dicionário de objetivos dentro da pasta do ranking.
 */
export interface ObjectiveAxis {
  /** Título da coluna de desvio ("Δ tempo até a meta"). */
  title: string;
  /** Nome curto da métrica, para cabeçalho de tabela e frase corrida ("Tempo"). */
  short: string;
  /** O que significa cair à esquerda da base. */
  left: string;
  /** O que significa cair à direita da base. */
  right: string;
}

export interface ObjectiveMeta {
  id: OfferedObjective;
  /** A pergunta como o usuário a lê no passo de escolha. */
  question: string;
  /** Uma linha explicando o que a resposta vai dizer. */
  hint: string;
  /** O único dado que este objetivo pede. */
  field: ObjectiveField;
  /** Rótulo do campo pedido. */
  fieldLabel: string;
  /** Rótulo do refinamento opcional, quando existe (hoje só a meta de TARGET_QUANTITY). */
  optionalFieldLabel?: string;
  /** Como o desvio vs base deste objetivo é rotulado no ranking. */
  axis: ObjectiveAxis;
}

const META: ObjectiveMeta[] = [
  {
    id: "OEE_IMPROVEMENT",
    question: "Melhorar o OEE",
    hint: "Responde qual OEE a linha alcança na janela e o que muda para melhorá-lo.",
    field: "window",
    fieldLabel: "Janela a avaliar",
    axis: { title: "Δ OEE projetado", short: "OEE", left: "menor", right: "maior" },
  },
  {
    id: "TARGET_QUANTITY",
    question: "Quanto eu produzo em…",
    hint: "Responde quantas unidades saem na janela escolhida.",
    field: "window",
    fieldLabel: "Janela a avaliar",
    // A meta virou refinamento opcional ("preciso de pelo menos X"), não pré-requisito.
    optionalFieldLabel: "Preciso de pelo menos (opcional)",
    axis: { title: "Δ produção na janela", short: "Produção", left: "menos", right: "mais" },
  },
  {
    id: "TIME_TO_QUANTITY",
    question: "Quanto tempo para produzir…",
    hint: "Responde em quanto tempo a quantidade fica pronta e quando termina.",
    field: "quantity",
    fieldLabel: "Quantidade a produzir",
    axis: { title: "Δ tempo até a meta", short: "Tempo", left: "mais rápido", right: "mais lento" },
  },
  {
    id: "OP_ON_TIME",
    question: "Entregar a Ordem de Produção no prazo",
    hint: "Responde se a entrega cabe no prazo e por qual folga — ou quanto atrasa.",
    field: "productionOrder",
    fieldLabel: "Ordem de Produção",
    axis: {
      title: "Δ folga sobre o prazo",
      short: "Folga",
      left: "menos folga",
      right: "mais folga",
    },
  },
];

/**
 * Objetivos oferecidos na tela.
 *
 * `OP_ON_TIME` só faz sentido com linha base (uma ordem pertence a uma linha real), então o
 * chamador filtra por contexto — ver `objectivesFor`.
 */
export const OBJECTIVE_META: readonly ObjectiveMeta[] = META;

/** Objetivos aplicáveis ao contexto. Cenário de linha nova não tem ordens a avaliar. */
export function objectivesFor(opts: { hasBaseLine: boolean }): readonly ObjectiveMeta[] {
  return opts.hasBaseLine ? META : META.filter((m) => m.id !== "OP_ON_TIME");
}

export function objectiveMeta(id: SimulationObjective): ObjectiveMeta | undefined {
  return META.find((m) => m.id === id);
}

/** Rótulo da métrica — vem do registry compartilhado, para não divergir do ranking. */
export function objectiveMetricLabel(id: SimulationObjective): string {
  return getObjectiveStrategy(id).label;
}

/**
 * Eixo de desvio do objetivo do lote. Objetivo de SEQUÊNCIA não tem entrada aqui (a fila tem
 * superfície própria) — cai num eixo genérico derivado do rótulo do registry, nunca num código cru.
 */
export function objectiveAxis(objective: AnySimulationObjective): ObjectiveAxis {
  if (!isSequenceObjective(objective)) {
    const meta = objectiveMeta(objective);
    if (meta) return meta.axis;
  }
  const label = isSequenceObjective(objective)
    ? getSequenceObjectiveStrategy(objective).label
    : getObjectiveStrategy(objective).label;
  return { title: `Δ ${label.toLowerCase()}`, short: label, left: "menor", right: "maior" };
}
